/*
 * Cart Threshold Offer - Single option.
 *
 * One threshold, one progress bar. Below the threshold it shows the pre message
 * plus progress; at or above it adds the free products to the cart and switches
 * to the unlocked state. Dropping back below removes them again.
 *
 * Free lines are tagged with the _ThresholdOffer property (same key the
 * multi-offer version uses) so the rest of the cart offer scripts keep
 * excluding them from cart totals.
 */
class CartThresholdSingleOffer extends HTMLElement {
  constructor() {
    super();
    this.OFFER_PROPERTY_KEY = '_ThresholdOffer';
    this.config = null;
    this.isProcessing = false;
    this.pendingRun = false;
    this.handleCartChange = this.handleCartChange.bind(this);
  }

  connectedCallback() {
    this.parseConfig();
    if (!this.config) return;

    this.card = this.querySelector('[data-ts-card]');
    this.icon = this.querySelector('[data-ts-icon]');
    this.messageEl = this.querySelector('[data-ts-message]');
    this.imageEl = this.querySelector('[data-ts-image]');
    this.progressWrap = this.querySelector('[data-ts-progress-wrap]');
    this.barEl = this.querySelector('[data-ts-bar]');
    this.amountsEl = this.querySelector('[data-ts-amounts]');

    document.addEventListener('theme:cart:change', this.handleCartChange);
    document.addEventListener('theme:cart:refresh', this.handleCartChange);

    this.bindTeaserPlayback();
    this.run();
  }

  disconnectedCallback() {
    document.removeEventListener('theme:cart:change', this.handleCartChange);
    document.removeEventListener('theme:cart:refresh', this.handleCartChange);
  }

  /*
   * Playback for the video teaser.
   *
   * The theme's <popup-component> still owns opening, closing, scroll lock and
   * focus - this only starts and stops the video with it. The dialog has no
   * native "open" event, so the open attribute is observed instead; that also
   * covers Esc and backdrop closes, which never reach the close button.
   */
  bindTeaserPlayback() {
    var dialog = this.querySelector('popup-component dialog');
    if (!dialog) return;

    var video = dialog.querySelector('video');
    if (!video) return;

    var stop = function () {
      try {
        video.pause();
        video.currentTime = 0;
      } catch (e) {}
    };

    var start = function () {
      var attempt = video.play();
      if (!attempt || typeof attempt.catch !== 'function') return;
      attempt.catch(function () {
        // Unmuted playback can still be refused if the click's user activation
        // has lapsed. Fall back to muted, which is always allowed.
        video.muted = true;
        var retry = video.play();
        if (retry && typeof retry.catch === 'function') {
          retry.catch(function () { /* leave it paused, controls are visible */ });
        }
      });
    };

    var wasOpen = dialog.hasAttribute('open');
    new MutationObserver(function () {
      var isOpen = dialog.hasAttribute('open') && !dialog.hasAttribute('closing');
      if (isOpen === wasOpen) return;
      wasOpen = isOpen;
      isOpen ? start() : stop();
    }).observe(dialog, {attributes: true, attributeFilter: ['open', 'closing']});

    dialog.addEventListener('close', stop);
  }

  parseConfig() {
    const script = this.querySelector('script[type="application/json"]');
    if (!script) return;

    try {
      const parsed = JSON.parse(script.textContent || '{}');
      const threshold = Number(parsed.cart_total_threshold);
      const freeProducts = (Array.isArray(parsed.free_products) ? parsed.free_products : [])
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0);

      if (!Number.isFinite(threshold) || threshold <= 0 || !freeProducts.length) return;

      this.config = {
        title: parsed.title || '',
        propertyName: parsed.property_name || 'threshold_single_offer',
        thresholdCents: Math.round(threshold * 100),
        preMessage: parsed.pre_message || '',
        postMessage: parsed.post_message || '',
        preImage: parsed.pre_image || '',
        postImage: parsed.post_image || '',
        freeProducts: freeProducts
      };
    } catch (error) {
      console.error('[ThresholdSingle] config parse failed', error);
    }
  }

  handleCartChange(event) {
    this.run(event && event.detail ? event.detail.cart : null);
  }

  async getCart(eventCart) {
    if (eventCart && Array.isArray(eventCart.items)) return eventCart;
    if (window.CartOfferUtils && typeof window.CartOfferUtils.getCartData === 'function') {
      // Fresh, not cached: the theme's theme:cart:change carries no cart, so a
      // stale snapshot here would decide "gift missing" and add a duplicate.
      return window.CartOfferUtils.getCartData(null, true);
    }
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
        headers: {'Content-Type': 'application/json'},
        credentials: 'same-origin'
      });
      return await response.json();
    } catch (error) {
      return null;
    }
  }

  // Cart value that counts toward the threshold: paid lines only. Every offer
  // freebie (this one, tier, buy X get Y) is excluded, otherwise adding a gift
  // could itself push the cart over the threshold.
  calculateCartTotal(cart) {
    if (!cart || !Array.isArray(cart.items)) return 0;

    return cart.items.reduce((total, item) => {
      // Gift cards are store credit, not qualifying spend toward a GWP threshold.
      if (item.gift_card === true) return total;
      if (item.product_type === 'OPTIONS_HIDDEN_PRODUCT') return total;

      const props = item.properties || {};
      if (props._MultipleOfferTier || props._Offer || props[this.OFFER_PROPERTY_KEY]) return total;

      return total + Number(item.final_line_price || 0);
    }, 0);
  }

  getOfferItems(cart) {
    if (!cart || !Array.isArray(cart.items)) return [];
    return cart.items.filter(
      (item) => item.properties && item.properties[this.OFFER_PROPERTY_KEY] === this.config.propertyName
    );
  }

  formatMoney(cents) {
    if (window.theme && typeof window.theme.formatMoney === 'function') {
      return window.theme.formatMoney(cents, window.theme.moneyFormat);
    }
    return `₹${Math.round(cents / 100).toLocaleString('en-IN')}`;
  }

  async run(eventCart) {
    if (this.isProcessing) {
      this.pendingRun = true;
      return;
    }
    this.isProcessing = true;

    try {
      const cart = await this.getCart(eventCart);
      if (!cart) return;

      const total = this.calculateCartTotal(cart);
      const reached = total >= this.config.thresholdCents;
      const offerItems = this.getOfferItems(cart);

      const mutated = reached ? await this.ensureGifts(offerItems) : await this.removeGifts(offerItems);

      if (mutated) {
        document.dispatchEvent(new CustomEvent('theme:cart:refresh', {bubbles: true}));
        return;
      }

      this.render(total, reached);
    } catch (error) {
      console.error('[ThresholdSingle] run failed', error);
    } finally {
      this.isProcessing = false;
      if (this.pendingRun) {
        this.pendingRun = false;
        this.run();
      }
    }
  }

  async ensureGifts(offerItems) {
    // One unit per gift. Duplicates merge into a single line when concurrent
    // cart changes are evaluated against a snapshot taken before an earlier
    // add, and the discount only covers the first unit - trim the rest.
    const trim = {};
    this.config.freeProducts.forEach((variantId) => {
      let allowed = 1;
      offerItems
        .filter((item) => Number(item.variant_id) === Number(variantId))
        .forEach((row) => {
          if (!row.key) return;
          const qty = Number(row.quantity || 0);
          const keep = Math.min(qty, allowed);
          allowed -= keep;
          if (keep !== qty) trim[row.key] = keep;
        });
    });

    if (Object.keys(trim).length) {
      await fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({updates: trim})
      });
      return true;
    }

    const present = new Set(offerItems.map((item) => Number(item.variant_id)));
    const missing = this.config.freeProducts.filter((id) => !present.has(id));
    if (!missing.length) return false;

    const items = missing.map((variantId) => ({
      variantId,
      quantity: 1,
      properties: {[this.OFFER_PROPERTY_KEY]: this.config.propertyName}
    }));

    if (window.CartOfferUtils && typeof window.CartOfferUtils.addProducts === 'function') {
      const result = await window.CartOfferUtils.addProducts(items);
      return Boolean(result && result.success);
    }

    const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest'},
      body: JSON.stringify({
        items: items.map((item) => ({
          id: item.variantId,
          quantity: item.quantity,
          properties: item.properties
        }))
      })
    });
    return response.ok;
  }

  // One /cart/update.js removes every gift line at once.
  async removeGifts(offerItems) {
    if (!offerItems.length) return false;

    const updates = {};
    offerItems.forEach((item) => {
      if (item.key) updates[item.key] = 0;
    });
    if (!Object.keys(updates).length) return false;

    try {
      await fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({updates})
      });
      return true;
    } catch (error) {
      console.error('[ThresholdSingle] remove failed', error);
      return false;
    }
  }

  render(total, reached) {
    if (!this.card) return;

    this.card.hidden = false;
    this.card.classList.toggle('is-unlocked', reached);

    const message = reached ? this.config.postMessage : this.config.preMessage;
    const remaining = Math.max(this.config.thresholdCents - total, 0);

    if (this.messageEl) {
      // [amount] in the pre message is replaced with what's still needed.
      this.messageEl.innerHTML = String(message || '').replace(/\[amount\]/g, this.formatMoney(remaining));
    }

    if (this.icon) this.icon.hidden = !reached;

    const imageUrl = reached ? this.config.postImage : this.config.preImage;
    if (this.imageEl) {
      if (imageUrl) {
        this.imageEl.src = imageUrl;
        this.imageEl.alt = this.config.title || '';
        this.imageEl.hidden = false;
      } else {
        this.imageEl.hidden = true;
        this.imageEl.removeAttribute('src');
      }
    }

    if (this.progressWrap) this.progressWrap.hidden = reached;

    if (!reached) {
      const percent = Math.min(100, Math.round((total / this.config.thresholdCents) * 100));
      if (this.barEl) this.barEl.style.width = `${percent}%`;
      if (this.amountsEl) {
        this.amountsEl.textContent = `${this.formatMoney(total)} / ${this.formatMoney(this.config.thresholdCents)}`;
      }
    }
  }
}

if (!customElements.get('cart-threshold-single-offer')) {
  customElements.define('cart-threshold-single-offer', CartThresholdSingleOffer);
}